import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { useIBTranslation } from '@ibinder/ui-common-hooks';
import {
  Flexbox,
  Button,
  TextInput
} from '@ibinder/common-frontend';
import SearchableDropDown from '../SearchableDropDown';

import {
  GET_ALL_MARKETS,
  POST_CUSTOMER
} from '../../../../../../api/queries/queries_local';

const Container = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledSubHeading = styled.h3`
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ResponseMessage = styled.div<{ hasErrors: boolean }>`
  padding: 1rem;
  background-color: ${(props) =>
    props.hasErrors ? '#ffebee' : '#e8f5e9'};
  color: ${(props) => (props.hasErrors ? '#c62828' : '#2e7d32')};
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const ButtonContainer = styled(Flexbox)`
  justify-content: flex-end;
  margin-top: 1rem;
`;

interface CreateCustomerFormProps {
  onClose: () => void;
  setIsCreateCustomer: (value: boolean) => void;
  handleSetCustomer: (customerId: string) => void;
}

const CreateCustomerForm: React.FC<CreateCustomerFormProps> = ({
  onClose,
  setIsCreateCustomer,
  handleSetCustomer
}) => {
  const [nameInputValue, setNameInputValue] = useState('');
  const [salesforceIdValue, setSalesforceIdValue] = useState('');
  const { t } = useIBTranslation();

  const [selectedValues, setSelectedValues] = useState<{
    market: number | null;
    name: string | null;
    salesforceAccountId: string | null;
  }>({
    market: null,
    name: null,
    salesforceAccountId: null
  });

  const marketInfo = useQuery(GET_ALL_MARKETS, { fetchPolicy: 'no-cache' });

  const [postCustomer, postCustomerData] = useMutation(POST_CUSTOMER);

  const handleNameChange = (e: any) => {
    const value = e.target.value;
    setNameInputValue(value);
    setSelectedValues((prevState) => ({
      ...prevState,
      name: value
    }));
  };

  const handleSalesforceIdChange = (e: any) => {
    const value = e.target.value;
    setSalesforceIdValue(value);
    setSelectedValues((prevState) => ({
      ...prevState,
      salesforceAccountId: value
    }));
  };

  const isFormValid = () => {
    return (
      selectedValues.name &&
      selectedValues.name.trim() !== '' &&
      selectedValues.market !== null
    );
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      return;
    }

    const marketData = marketInfo.data?.get;
    const selectedMarket = marketData?.[selectedValues.market!];

    const body = {
      name: selectedValues.name,
      marketId: selectedMarket?.id,
      salesforceAccountId: selectedValues.salesforceAccountId || ''
    };

    postCustomer({
      variables: {
        body
      }
    }).then((response) => {
      if (response.data?.postCustomer?.id) {
        onClose();
      }
    });
  };

  const renderStatus = () => {
    if (postCustomerData.error) {
      return (
        <div>
          <strong>Error creating customer:</strong>
          <p>{postCustomerData.error.message}</p>
        </div>
      );
    }

    if (postCustomerData.data) {
      return (
        <div>
          <strong>Customer created successfully!</strong>
          <p>Customer ID: {postCustomerData.data.postCustomer?.id}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <Container>
      {postCustomerData.called ? (
        <ResponseMessage hasErrors={!!postCustomerData.error}>
          {renderStatus()}
        </ResponseMessage>
      ) : (
        <>
          <StyledSubHeading>Customer Information</StyledSubHeading>
          <TextInput
            name="customerName"
            label="Customer Name"
            value={nameInputValue}
            placeholder="Enter customer name"
            changeValue={handleNameChange}
            required={true}
          />
          <SearchableDropDown
            label="Select market for customer"
            data={marketInfo.data?.get}
            loading={marketInfo.loading}
            setSelectedIndex={(index) =>
              setSelectedValues((prevState) => ({
                ...prevState,
                market: index
              }))
            }
          />
          <TextInput
            name="salesforceId"
            label="Salesforce Account ID"
            value={salesforceIdValue}
            placeholder="Enter Salesforce Account ID (optional)"
            changeValue={handleSalesforceIdChange}
            required={false}
          />
          <ButtonContainer>
            <Button
              onClick={onClose}
              label="Cancel"
              btnStyle="secondary"
            />
            <Button
              onClick={handleSubmit}
              label={postCustomerData.loading ? 'Creating...' : 'Create Customer'}
              btnStyle="primary"
              disabled={!isFormValid() || postCustomerData.loading}
            />
          </ButtonContainer>
        </>
      )}
    </Container>
  );
};

export default CreateCustomerForm;
